import React from 'react';

/*
 * Which market this build serves.
 *
 *   'russia' — Russian only. No language picker; the header looks exactly as it always has.
 *   'europe' — the player picks a language; the backend translates authored novel text into it.
 *
 * Set at build time via REACT_APP_REGION so the region can never disagree with the deploy target.
 * Use the npm scripts — `npm run build:eu` / `npm run build:ru` — which pin both this and the API
 * URL together; editing this file by hand is what lets a Russian build reach a European backend.
 *
 * The backend has its own REGION env var and does not trust this one — this only decides whether
 * the picker is rendered.
 */
export const REGION = process.env.REACT_APP_REGION || 'europe';

export const IS_EUROPE = REGION === 'europe';

/* Yandex.Metrika counter. Russia's loads from index.html; europe's from utils/yandexMetrika.js,
   after analytics consent. Goals are sent to whichever counter this build has. */
export const METRIKA_ID = IS_EUROPE ? 113749070 : 106953573;

/*
 * The two regions are two products. Europe is Plotex; Russia stays AI Novel, with its own payment
 * flow and the legal disclosure a registered ИП has to publish. Everything region-specific is
 * routed through these constants rather than through scattered IS_EUROPE checks in the markup.
 */
export const BRAND = IS_EUROPE ? 'Plotex' : 'AI Novel';

/*
 * Whether the top-up page and the links into it exist at all.
 *
 * On in both regions: europe publishes its price list so players can see what stars cost, even
 * though nothing can be bought yet.
 */
export const HAS_PAYMENTS = true;

/*
 * Both regions can now complete a purchase: russia checks out through YooKassa, which settles in
 * rubles for a Russian merchant, and europe through Polar, a merchant of record that handles EU
 * VAT. The backend picks between them on its own REGION; the frontend only ever receives a
 * confirmation URL to redirect to, which is why no flag distinguishes them here any more.
 */

/* The rewarded-ad top-up is wired to a Russian ad network. */
export const HAS_AD_REWARD = !IS_EUROPE;

/*
 * The paid monthly subscription (see backend/src/utils/subscriptions.py). Russia-only: the plans
 * are priced in rubles and settle through YooKassa, and europe sells one-off star packs through
 * Polar instead. The backend gates the /subscription endpoints on its own REGION; this only
 * decides whether the plan cards and the daily-claim button are rendered.
 */
export const HAS_SUBSCRIPTIONS = !IS_EUROPE;

/* Rendered next to every price. The backend decides the actual currency (PAYMENT_CURRENCY); this
   is only how it is drawn. */
export const CURRENCY_SYMBOL = IS_EUROPE ? '$' : '₽';
export const CURRENCY_BEFORE_AMOUNT = IS_EUROPE;

/* Named on the top-up page because the processor taking the card has to be identified where the
   charge is made. Europe's merchant of record is Polar — it is the party on the customer's card
   statement and on the receipt, so the legal documents name it too; russia bills through
   YooKassa. */
export const PAYMENT_PROCESSOR = IS_EUROPE ? 'Polar' : 'ЮKassa';

/* Account verification runs through a Russian-language Telegram bot and community group, and the
   reward it pays out is stars, which Europe has no way to spend more of. The backend refuses the
   link/unlink endpoints there too, and never gates a scene on a Telegram subscription. */
export const HAS_TELEGRAM = !IS_EUROPE;

/*
 * Canonical origin and support mailbox. These feed canonical/og:url meta, so they have to match
 * the domain the build is actually served from. The backend keeps its own copies: FRONTEND_URL in
 * docker-compose.yml (used in confirmation-mail links) and FROM_EMAIL in utils/email_sender.py.
 */
export const SITE_URL = IS_EUROPE ? 'https://plotex.io' : 'https://ainovel.ru';
export const SUPPORT_EMAIL = IS_EUROPE ? 'info@plotex.io' : 'ainovel@yandex.ru';

/*
 * Role addresses published on /legal, /copyright and the privacy request form.
 *
 * These are the addresses the signed documents actually name, not new ones invented for the site:
 * the Privacy Policy tells a data subject to write to info@plotex.io, and a takedown notice
 * has to reach the address the Terms point a rightsholder at. Publishing anything else would send
 * a legally significant notice somewhere the document does not bind us to read.
 *
 * Splitting them into three constants is deliberate — the instruction requires a distinct contact
 * per purpose, and when dedicated mailboxes (privacy@ / copyright@) exist, only these lines change.
 */
export const LEGAL_EMAIL = IS_EUROPE ? 'info@plotex.io' : 'ainovel@yandex.ru';
export const PRIVACY_EMAIL = LEGAL_EMAIL;
export const COPYRIGHT_EMAIL = LEGAL_EMAIL;

/*
 * Operator identity, published in full because an EU-facing service must be identifiable
 * (DSA art. 12 / e-Commerce Directive art. 5). Mirrors the details in the legal documents.
 */
export const COMPANY = IS_EUROPE ? {
  legalName: '«PLOTEX», Limited Liability Partnership',
  localName: 'Товарищество с ограниченной ответственностью «PLOTEX»',
  registrationLabel: 'BIN',
  registrationNumber: '260840000843',
  address: '2/22 Saken Zhunisov Street, Building 3, Non-residential Unit 92, Shugyla Microdistrict, Nauryzbay District, Almaty 050000, Kazakhstan',
  localAddress: 'Республика Казахстан, г. Алматы, Наурызбайский район, мкр. Шугыла, ул. Сакен Жунисов, д. 2/22, корп. 3, н.п. 92, 050000',
  phone: '+7 (747) 992-75-15',
} : null;

export const UI_LANGUAGE_KEY = 'ui-language';

export const DEFAULT_LANGUAGE = IS_EUROPE ? 'english' : 'russian';

/*
 * Flags are inline SVG rather than emoji on purpose: index.css aliases every font to a system
 * stack with zero external requests, and Windows ships no flag glyphs in Segoe UI Emoji — 🇪🇸
 * silently degrades to the letters "ES" for a large share of desktop users.
 */
const flag = (children) => (props) => (
  <svg viewBox="0 0 24 16" width="24" height="16" aria-hidden="true" {...props}>
    {children}
  </svg>
);

const FlagEN = flag(
  <>
    <rect width="24" height="16" fill="#012169" />
    <path d="M0 0l24 16M24 0L0 16" stroke="#fff" strokeWidth="3.2" />
    <path d="M0 0l24 16M24 0L0 16" stroke="#C8102E" strokeWidth="1.8" />
    <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5.3" />
    <path d="M12 0v16M0 8h24" stroke="#C8102E" strokeWidth="3.2" />
  </>
);

const FlagDE = flag(
  <>
    <rect width="24" height="5.33" fill="#000" />
    <rect y="5.33" width="24" height="5.34" fill="#DD0000" />
    <rect y="10.67" width="24" height="5.33" fill="#FFCE00" />
  </>
);

const FlagFR = flag(
  <>
    <rect width="8" height="16" fill="#002395" />
    <rect x="8" width="8" height="16" fill="#fff" />
    <rect x="16" width="8" height="16" fill="#ED2939" />
  </>
);

const FlagES = flag(
  <>
    <rect width="24" height="16" fill="#AA151B" />
    <rect y="4" width="24" height="8" fill="#F1BF00" />
  </>
);

const FlagIT = flag(
  <>
    <rect width="8" height="16" fill="#008C45" />
    <rect x="8" width="8" height="16" fill="#F4F5F0" />
    <rect x="16" width="8" height="16" fill="#CD212A" />
  </>
);

const FlagPL = flag(
  <>
    <rect width="24" height="8" fill="#fff" />
    <rect y="8" width="24" height="8" fill="#DC143C" />
  </>
);

const FlagRU = flag(
  <>
    <rect width="24" height="5.33" fill="#fff" />
    <rect y="5.33" width="24" height="5.34" fill="#0039A6" />
    <rect y="10.67" width="24" height="5.33" fill="#D52B1E" />
  </>
);

/* code matches the backend's SUPPORTED_LANGUAGES; label is written in the language itself */
export const LANGUAGES = [
  { code: 'english', label: 'English',   Flag: FlagEN },
  { code: 'german',  label: 'Deutsch',   Flag: FlagDE },
  { code: 'french',  label: 'Français',  Flag: FlagFR },
  { code: 'spanish', label: 'Español',   Flag: FlagES },
  { code: 'italian', label: 'Italiano',  Flag: FlagIT },
  { code: 'polish',  label: 'Polski',    Flag: FlagPL },
  { code: 'russian', label: 'Русский',   Flag: FlagRU },
];

/*
 * Short URL codes. Every Plotex page lives under one of these prefixes (/en/blog, /de/blog, …)
 * so that each language is a distinct, crawlable URL rather than the same URL rendering
 * differently per visitor — search engines cannot see localStorage.
 */
export const LANGUAGE_CODES = {
  english: 'en',
  german:  'de',
  french:  'fr',
  spanish: 'es',
  italian: 'it',
  polish:  'pl',
  russian: 'ru',
};

export const CODE_TO_LANGUAGE = Object.fromEntries(
  Object.entries(LANGUAGE_CODES).map(([name, code]) => [code, name])
);

/* The code sitting in the first path segment, or null when there is none (a bare "/" on a first
   visit, or any russia-build URL). Read from the URL rather than from storage because the URL is
   what a crawler, a shared link and a hreflang alternate all agree on. */
export const getLanguageFromPath = (pathname = window.location.pathname) => {
  const first = pathname.split('/')[1];
  return CODE_TO_LANGUAGE[first] ? first : null;
};

/* What the browser would prefer, before any explicit choice */
const browserLanguage = () => {
  const primary = (navigator.language || '').split('-')[0].toLowerCase();
  return CODE_TO_LANGUAGE[primary] || DEFAULT_LANGUAGE;
};

/*
 * Resolution order: the URL wins, then a previous explicit choice, then the browser.
 *
 * The URL comes first so that a link shared into a German forum opens in German for everyone who
 * clicks it, regardless of what each visitor happens to have in localStorage.
 */
export const getLanguage = () => {
  if (!IS_EUROPE) return 'russian';
  const fromPath = getLanguageFromPath();
  if (fromPath) return CODE_TO_LANGUAGE[fromPath];
  const stored = localStorage.getItem(UI_LANGUAGE_KEY);
  if (stored && LANGUAGES.some(l => l.code === stored)) return stored;
  return browserLanguage();
};

/* True while the player has never actually chosen — the value above is only a guess from the
   browser, so it is worth saving server-side the first time it is used. */
export const isLanguageUnchosen = () => IS_EUROPE && !localStorage.getItem(UI_LANGUAGE_KEY);

export const setLanguage = (code) => localStorage.setItem(UI_LANGUAGE_KEY, code);

/* The language this page is being rendered in. Resolved once: switching languages navigates to
   the other prefix, which is a full page load. */
export const LANGUAGE = getLanguage();
export const LANGUAGE_CODE = LANGUAGE_CODES[LANGUAGE];

/*
 * Router basename. Every in-app <Link to="/blog"> and navigate('/blog') is resolved against it by
 * react-router, so the prefix is applied in exactly one place instead of at ~70 call sites.
 * The russia build keeps its bare, unprefixed URLs.
 */
export const ROUTER_BASENAME = IS_EUROPE ? `/${LANGUAGE_CODE}` : '';

/* For the handful of places that must build a URL outside the router: raw <a href>, canonical and
   hreflang tags, window.location assignments, sitemap entries. */
export const localePath = (path = '/') => {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return IS_EUROPE ? `/${LANGUAGE_CODE}${clean === '/' ? '' : clean}` : clean;
};

export const localeUrl = (path = '/', language = LANGUAGE) => {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (!IS_EUROPE) return `${SITE_URL}${clean}`;
  return `${SITE_URL}/${LANGUAGE_CODES[language]}${clean === '/' ? '' : clean}`;
};

/*
 * Send a bare URL to its language-prefixed twin.
 *
 * Runs before React mounts so a crawler following "/" is redirected once, to a real localized URL,
 * instead of being served an unprefixed duplicate of every page. Uses replace() so the prefix-less
 * URL never enters history.
 */
export const ensureLanguagePrefix = () => {
  if (!IS_EUROPE) return;
  if (getLanguageFromPath()) return;
  const { pathname, search, hash } = window.location;
  const code = LANGUAGE_CODES[getLanguage()];
  const rest = pathname === '/' ? '' : pathname;
  window.location.replace(`/${code}${rest}${search}${hash}`);
};
